import { type ElementType, type HTMLAttributes, type JSX, type Ref } from 'react'
import { useTheme } from '@mui/material/styles'

import useButtonsGroup from '@Hooks/useButtonsGroup'

import Button, { type ButtonProps } from '../Button'

import * as styles from './styles'

type Variant = 'NORMAL' | 'IN_A_CARD' | 'BOTTOM_ACTIONS'

export type ButtonsGroupProps<
  PrimaryComponentType extends ElementType = 'button',
  SecondaryComponentType extends ElementType = 'button',
> = {
  primary: ButtonProps<PrimaryComponentType>
  secondary?: ButtonProps<SecondaryComponentType>
  variant?: Variant
} & HTMLAttributes<HTMLDivElement>

// TODO https://jira.vsct.fr/browse/IVTS-47905 revoir les styles pour l'affichage des boutons
const ButtonsGroup = <
  PrimaryComponentType extends ElementType = 'button',
  SecondaryComponentType extends ElementType = 'button',
>({
  primary,
  secondary,
  variant = 'NORMAL',
  ...rest
}: ButtonsGroupProps<PrimaryComponentType, SecondaryComponentType>): JSX.Element => {
  const theme = useTheme()
  const { overriddenWidth, firstButtonRef, secondButtonRef } = useButtonsGroup()

  return (
    <div
      {...rest}
      css={[
        styles.buttonsGroup(theme),
        variant === 'IN_A_CARD' && styles.inACard(theme),
        variant === 'BOTTOM_ACTIONS' && styles.bottomActions(theme),
      ]}
    >
      {/* Primary button is first in dom for a11y but displayed on the right when horizontal */}
      <Button
        ref={firstButtonRef as Ref<HTMLButtonElement>}
        css={[
          styles.buttonInGroup(theme),
          variant === 'IN_A_CARD' && styles.fullWidth,
          overriddenWidth && styles.customWidth(overriddenWidth),
        ]}
        {...primary}
      />
      {secondary && (
        <Button
          ref={secondButtonRef as Ref<HTMLButtonElement>}
          css={[
            styles.buttonInGroup(theme),
            variant === 'IN_A_CARD' && styles.fullWidth,
            overriddenWidth && styles.customWidth(overriddenWidth),
          ]}
          {...secondary}
          variant="secondary"
        />
      )}
    </div>
  )
}

export default ButtonsGroup
